/**
 * Turns the Hamming widget's text fields into an encode-and-corrupt run or a check of a
 * received word, or a named error the widget can phrase.
 */

import {
  type CheckOutcome,
  checkHamming,
  type EncodeOutcome,
  encodeHamming,
  flipPositions,
  MAX_DATA_BITS,
  type Options,
  parseBits,
  parsePositions,
} from "./hamming";

export type HammingErrorKey =
  | "errEmptyData"
  | "errInvalidData"
  | "errTooLongData"
  | "errEmptyReceived"
  | "errInvalidReceived"
  | "errTooLongReceived"
  | "errLength"
  | "errPositions"
  | "errOutOfRange";

export type HammingMode = "encode" | "check";

export interface HammingInput {
  mode: HammingMode;
  options: Options;
  dataText: string;
  flipText: string;
  receivedText: string;
}

export type HammingView =
  | { kind: "error"; key: HammingErrorKey }
  | {
      kind: "encode";
      encoded: EncodeOutcome;
      /** The positions flipped to simulate noise. */
      flips: number[];
      /** The word after the flips, when any were requested. */
      received: string | null;
      check: CheckOutcome | null;
    }
  | { kind: "check"; check: CheckOutcome };

const MAX_RECEIVED = MAX_DATA_BITS + 9;

function evaluateCheck(input: HammingInput): HammingView {
  const parsed = parseBits(input.receivedText, MAX_RECEIVED);
  if (!parsed.ok) {
    const keys = {
      empty: "errEmptyReceived",
      invalid: "errInvalidReceived",
      tooLong: "errTooLongReceived",
    } as const;
    return { kind: "error", key: keys[parsed.error] };
  }
  const check = checkHamming(parsed.bits, input.options);
  return check ? { kind: "check", check } : { kind: "error", key: "errLength" };
}

function evaluateEncode(input: HammingInput): HammingView {
  const parsed = parseBits(input.dataText, MAX_DATA_BITS);
  if (!parsed.ok) {
    const keys = {
      empty: "errEmptyData",
      invalid: "errInvalidData",
      tooLong: "errTooLongData",
    } as const;
    return { kind: "error", key: keys[parsed.error] };
  }
  const encoded = encodeHamming(parsed.bits, input.options);
  const min = input.options.extended ? 0 : 1;
  const positions = parsePositions(input.flipText, min, encoded.layout.n);
  if (!positions.ok) {
    return {
      kind: "error",
      key: positions.error === "invalid" ? "errPositions" : "errOutOfRange",
    };
  }
  if (positions.positions.length === 0) {
    return {
      kind: "encode",
      encoded,
      flips: [],
      received: null,
      check: null,
    };
  }
  const received = flipPositions(
    encoded.codeword,
    positions.positions,
    input.options.extended
  );
  return {
    kind: "encode",
    encoded,
    flips: positions.positions,
    received,
    check: checkHamming(received, input.options),
  };
}

export function evaluateHamming(input: HammingInput): HammingView {
  return input.mode === "check" ? evaluateCheck(input) : evaluateEncode(input);
}
